import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { X, Loader2 } from 'lucide-react'
import { getGroup, saveGroup, listUsers, type YepGroup, type Perm, type Grants } from '../../lib/api/users'
import { getContentModels } from '../../lib/api/content'
import { Field, TextInput } from '../config/ui'
import { cn } from '../../lib/cn'

const PERM_KEYS: (keyof Perm)[] = ['read', 'write', 'edit', 'delete']
const PERM_LABELS: Record<keyof Perm, string> = { read: 'Lettura', write: 'Creazione', edit: 'Modifica', delete: 'Eliminazione' }
const emptyPerm = (): Perm => ({ read: false, write: false, edit: false, delete: false })
const isPerm = (v: unknown): v is Perm => Boolean(v) && typeof v === 'object' && 'read' in (v as object)

export function GroupDrawer({ target, onClose }: { target: YepGroup | 'new' | null; onClose: () => void }) {
  const qc = useQueryClient()
  const isNew = target === 'new'
  const id = target && target !== 'new' ? target.id : undefined

  const full = useQuery({ queryKey: ['group', id], queryFn: () => getGroup(id!), enabled: Boolean(id) })
  const users = useQuery({ queryKey: ['operators'], queryFn: listUsers })
  const models = useQuery({ queryKey: ['contentModels'], queryFn: getContentModels, staleTime: Infinity })

  const [name, setName] = useState('')
  const [userIds, setUserIds] = useState<string[]>([])
  const [grants, setGrants] = useState<Grants>({ user: emptyPerm(), content: {} })
  const [base, setBase] = useState<YepGroup | null>(null) // full entity to round-trip on save
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const g: YepGroup | null = target && target !== 'new' ? full.data ?? target : null
    if (g) {
      setBase(g)
      setName(g.name ?? '')
      setUserIds(g.userIds ?? [])
      setGrants(g.grants ?? { user: emptyPerm(), content: {} })
    } else if (isNew) {
      const content: Record<string, Perm> = {}
      for (const m of models.data ?? []) content[m.id] = emptyPerm()
      setBase(null)
      setName('')
      setUserIds([])
      setGrants({ user: emptyPerm(), content })
    }
    setError(null)
  }, [isNew, full.data, target, models.data])

  // matrix rows: the top-level "user" grant + each content model that carries a read/write/edit/delete object
  const contentRows = useMemo(() => {
    const labels = new Map((models.data ?? []).map((m) => [m.id, m.label]))
    return Object.keys(grants.content ?? {})
      .filter((k) => isPerm(grants.content?.[k]))
      .map((k) => ({ key: k, label: labels.get(k) ?? k }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [grants.content, models.data])

  function toggleUser(p: keyof Perm) {
    setGrants((g) => ({ ...g, user: { ...emptyPerm(), ...g.user, [p]: !g.user?.[p] } }))
  }
  function toggleContent(model: string, p: keyof Perm) {
    setGrants((g) => ({
      ...g,
      content: { ...g.content, [model]: { ...emptyPerm(), ...(g.content?.[model] as Perm), [p]: !(g.content?.[model] as Perm)?.[p] } },
    }))
  }
  function toggleMember(uid: string) {
    setUserIds((ids) => (ids.includes(uid) ? ids.filter((x) => x !== uid) : [...ids, uid]))
  }

  const save = useMutation({
    mutationFn: () => saveGroup({ ...(base || {}), ...(id ? { id } : {}), name, userIds, grants } as YepGroup),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['groups'] })
      onClose()
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Errore durante il salvataggio'),
  })

  if (!target) return null
  const loading = (Boolean(id) && full.isLoading) || models.isLoading

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} />
      <aside className="fixed inset-y-0 right-0 z-50 flex w-full max-w-2xl flex-col bg-paper shadow-2xl">
        <div className="flex items-center justify-between border-b border-line bg-card px-5 py-4">
          <h3 className="font-display text-[16px] font-bold text-coal">{isNew ? 'Nuovo gruppo' : 'Modifica gruppo'}</h3>
          <button onClick={onClose} className="text-faint hover:text-slate" aria-label="Chiudi">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto p-5">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-faint" />
            </div>
          ) : (
            <>
              <Field label="Nome gruppo">
                <TextInput value={name} onChange={setName} placeholder="Es. Operatori cassa" />
              </Field>

              {/* members */}
              <div>
                <span className="mb-1 block text-[11px] font-bold uppercase tracking-[1.2px] text-muted">Operatori del gruppo</span>
                <div className="max-h-40 overflow-y-auto rounded-lg border border-line bg-card p-2">
                  {(users.data ?? []).length === 0 ? (
                    <p className="px-1 py-2 text-[12.5px] text-faint">Nessun operatore.</p>
                  ) : (
                    (users.data ?? []).map((u) => (
                      <label key={u.id ?? u.username} className="flex cursor-pointer items-center gap-2.5 rounded px-1.5 py-1.5 hover:bg-paper">
                        <input
                          type="checkbox"
                          checked={Boolean(u.id && userIds.includes(u.id))}
                          onChange={() => u.id && toggleMember(u.id)}
                          className="h-4 w-4 accent-verdigris"
                        />
                        <span className="text-[13px] text-coal">{u.name || u.username}</span>
                        <span className="text-[12px] text-faint">{u.username}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              {/* permission matrix */}
              <div>
                <span className="mb-1 block text-[11px] font-bold uppercase tracking-[1.2px] text-muted">Permessi</span>
                <div className="overflow-hidden rounded-lg border border-line">
                  <table className="w-full text-[12.5px]">
                    <thead>
                      <tr className="border-b border-line-soft bg-paper/70 text-[10.5px] font-bold uppercase tracking-wide text-muted">
                        <th className="px-3 py-2 text-left">Sezione</th>
                        {PERM_KEYS.map((p) => (
                          <th key={p} className="px-2 py-2 text-center">{PERM_LABELS[p]}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <PermRow label="Utenti (operatori)" perm={grants.user ?? emptyPerm()} onToggle={toggleUser} />
                      {contentRows.map((r) => (
                        <PermRow
                          key={r.key}
                          label={r.label}
                          perm={(grants.content?.[r.key] as Perm) ?? emptyPerm()}
                          onToggle={(p) => toggleContent(r.key, p)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {error && <p className="text-[12.5px] font-medium text-venetian">{error}</p>}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-line bg-card px-5 py-3.5">
          <button onClick={onClose} className="rounded-lg border border-line bg-card px-4 py-2 text-[13px] font-semibold text-slate hover:border-faint">
            Annulla
          </button>
          <button
            onClick={() => save.mutate()}
            disabled={!name.trim() || save.isPending || loading}
            className="inline-flex items-center gap-2 rounded-lg bg-verdigris px-4 py-2 text-[13px] font-semibold text-white hover:bg-verdigris-700 disabled:opacity-50"
          >
            {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Salva
          </button>
        </div>
      </aside>
    </>
  )
}

function PermRow({ label, perm, onToggle }: { label: string; perm: Perm; onToggle: (p: keyof Perm) => void }) {
  return (
    <tr className="border-b border-line-soft last:border-0">
      <td className="px-3 py-2 text-coal">{label}</td>
      {PERM_KEYS.map((p) => (
        <td key={p} className={cn('px-2 py-2 text-center')}>
          <input type="checkbox" checked={Boolean(perm?.[p])} onChange={() => onToggle(p)} className="h-4 w-4 accent-verdigris" />
        </td>
      ))}
    </tr>
  )
}
